/* Hadith on the virtues of a dhikr — and the cards the "Effects of sins" page shares.
   The texts are quoted from the Sunan collections by scripts/fetch-virtues.mjs (src/data/virtues.json),
   each with the scholars' own grading. Where scholars differ, or a narration is weak, the card says so. */
import data from '@/data/virtues.json';

export type Tier = 'sahih' | 'hasan' | 'mixed' | 'weak' | 'fabricated' | 'ungraded';
export interface Hadith { ref: string; text: string; narrator?: string; partial?: boolean; note?: string; also?: string[]; athar?: boolean; tier: Tier; by: string[] }

const virtues = data as unknown as {
  credit: string;
  hadith: Record<string, Hadith>;
  adhkar: Record<string, string[]>;
  dhikr: Record<string, string[]>;
  sins: { intro: string; sections: SinSection[] };
  distress: Distress;
  ayat: Record<string, string>;
};
export interface DistressDua { id: string; title: string; hisn?: number; ar?: string; tr?: string; en?: string; h: string[]; source?: string }
export interface DistressStep { id: string; title: string; why: string; proof?: string[]; duas: DistressDua[]; ayat?: string[]; need?: boolean }
export interface Distress {
  intro: string; assurance: { ayat: string[]; h: string[] }; before: { title: string; points: string[]; h: string[] };
  steps: DistressStep[]; after: { title: string; why: string; ayat: string[]; h: string[] };
}
export interface SinSection { id: string; title: string; ar: string; intro: string; items: ({ h: string } | { ayat: string[]; lead?: string })[] }

export const VIRTUES_CREDIT = virtues.credit;
export const SINS = virtues.sins;
export const DISTRESS = virtues.distress;
/** English (Saheeh International) of the āyāt the pages quote; their Arabic is bundled in quranAdhkar.json. */
export const AYAT = virtues.ayat;
export const hadithById = (id: string): Hadith => virtues.hadith[id];

/** Hadith ids for an adhkar item (by its id in adhkar.json) or a dhikr-counter dhikr (by its string id). */
export const hadithIdsFor = (kind: 'adhkar' | 'dhikr', id: string | number): string[] => virtues[kind][String(id)] ?? [];

const CHIP: Record<Tier, { label: string; colour: string } | null> = {
  sahih: { label: 'Ṣaḥīḥ', colour: 'var(--win-ok)' },
  hasan: { label: 'Ḥasan', colour: 'var(--win-ok)' },
  mixed: { label: 'Scholars differ', colour: 'var(--win-warn)' },
  weak: { label: 'Ḍaʿīf · weak', colour: 'var(--win-crit)' },
  fabricated: { label: 'Mawḍūʿ', colour: 'var(--win-crit)' },
  ungraded: null,
};

export function HadithCard({ id }: { id: string }) {
  const h = hadithById(id);
  if (!h) return null;
  const chip = CHIP[h.tier];
  return (
    <article className="rounded-2xl bg-fill p-4 text-left" data-hadith={id}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[13px] font-semibold text-gold">{h.ref}</span>
        {chip && <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ color: chip.colour, background: `color-mix(in srgb, ${chip.colour} 16%, transparent)` }}>{chip.label}</span>}
        {h.athar && <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">Companion’s saying</span>}
        {h.partial && <span className="text-[11px] text-muted">excerpt</span>}
      </div>
      <blockquote className="mt-2 text-[15px] leading-relaxed">{h.text}</blockquote>
      {h.narrator && <p className="mt-1.5 text-[12px] text-muted">Narrated by {h.narrator}</p>}
      {h.note && <p className="mt-2 text-[13px] leading-snug text-muted">{h.note}</p>}
      {h.also && h.also.length > 0 && <p className="mt-1.5 text-[12px] leading-snug text-muted">Also in {h.also.join(' · ')}</p>}
      {h.by.length > 0 && <p className="mt-1.5 text-[12px] leading-snug text-muted">Graded: {h.by.join(' · ')}</p>}
      {h.tier === 'weak' && <p className="mt-1.5 text-[12px] font-medium leading-snug text-[var(--win-crit)]">Every scholar listed grades this weak. It is shown because it is the narration this dhikr comes from — not as a basis for the reward.</p>}
      {h.tier === 'mixed' && <p className="mt-1.5 text-[12px] leading-snug text-[var(--win-warn)]">Scholars differ on this narration’s grading, as listed above.</p>}
    </article>
  );
}

/** The hadith on a dhikr's virtue, in a list. Renders nothing when there are none. */
export function HadithList({ ids, title = 'Hadith on its virtue · أحاديث في فضله' }: { ids: string[]; title?: string }) {
  if (!ids.length) return null;
  return (
    <div>
      <div className="pb-2 text-[12px] font-semibold uppercase tracking-wide text-gold">{title}</div>
      <div className="space-y-3">{ids.map((id) => <HadithCard key={id} id={id} />)}</div>
      <p className="pt-2 text-[12px] leading-snug text-muted">{VIRTUES_CREDIT} Gradings are the named scholars’ own.</p>
    </div>
  );
}
